import React, { useEffect, useRef, useState } from "react";
import { CheckIcon, DoorIcon, GateIcon, KeyIcon, LockLineIcon, LockSolidIcon, UnlockLineIcon } from "./accessIcons";
import styles from "../publicvehicle.module.css";

const OPEN_THRESHOLD = 0.85;
const GATE_RESET_MS = 5000;
const HOLD_MS = 1000;
const DOOR_RELOCK_SECONDS = 5;
const KNOB_SIZE = 46;

// Clears pending timers and blocks state updates after unmount
const useSafeTimers = () => {
  const mounted = useRef(true);
  const timers = useRef([]);
  useEffect(() => () => {
    mounted.current = false;
    timers.current.forEach(clearTimeout);
  }, []);
  const later = (fn, ms) => {
    const id = setTimeout(() => { if (mounted.current) fn(); }, ms);
    timers.current.push(id);
    return id;
  };
  return { mounted, later };
};

/*
 * Parking gate: drag the key knob to the lock at the right end (or press Enter / Space on the track) to open.
 * Styled after the tenant app's ParkingGate slider. onOpen() -> Promise<boolean>; the parent shows errors.
 */
export const GateControl = ({ onOpen }) => {
  // idle | opening | opened
  const [phase, setPhase] = useState("idle");
  const [x, setX] = useState(0);
  const [dragging, setDragging] = useState(false);
  const trackRef = useRef(null);
  const knobRef = useRef(null);
  const drag = useRef(null);
  const xRef = useRef(0);
  const { mounted, later } = useSafeTimers();

  const maxX = () => {
    const track = trackRef.current;
    const knob = knobRef.current;
    if (!track || !knob) return 0;
    return Math.max(0, track.clientWidth - knob.offsetWidth);
  };

  const moveTo = (value) => {
    xRef.current = value;
    setX(value);
  };

  const trigger = async () => {
    moveTo(maxX());
    setPhase("opening");
    const ok = await onOpen();
    if (!mounted.current) return;
    moveTo(0);
    if (ok) {
      setPhase("opened");
      later(() => setPhase("idle"), GATE_RESET_MS);
    } else {
      setPhase("idle");
    }
  };

  const onPointerDown = (e) => {
    if (phase !== "idle") return;
    e.preventDefault();
    e.currentTarget.setPointerCapture?.(e.pointerId);
    drag.current = { startX: e.clientX - xRef.current, max: maxX() };
    setDragging(true);
  };

  const onPointerMove = (e) => {
    if (!drag.current) return;
    moveTo(Math.min(Math.max(e.clientX - drag.current.startX, 0), drag.current.max));
  };

  const endDrag = (e) => {
    if (!drag.current) return;
    const { max } = drag.current;
    drag.current = null;
    setDragging(false);
    if (e.type === "pointerup" && max > 0 && xRef.current >= max * OPEN_THRESHOLD) {
      trigger();
    } else {
      moveTo(0);
    }
  };

  const onKeyDown = (e) => {
    if (e.key !== "Enter" && e.key !== " ") return;
    e.preventDefault();
    if (phase === "idle") trigger();
  };

  const status = { idle: "Locked", opening: "Opening…", opened: "Open — drive through" }[phase];

  return (<div className={styles.gaControl}>
    <div className={styles.gaDevice}>
      <span className={styles.gaDeviceIcon}><GateIcon /></span>
      <div>
        <p className={styles.gaStatus} aria-live="polite">{status}</p>
        <p className={styles.gaDeviceName}>Parking Gate</p>
      </div>
    </div>

    {phase === "opened"
      ? (<div className={styles.gaOpened}><span className={styles.gaOpenedBtn}><UnlockLineIcon /></span></div>)
      : (<div
        ref={trackRef}
        role="button"
        tabIndex={0}
        aria-label="Slide to open the parking gate"
        aria-disabled={phase !== "idle"}
        className={styles.gaTrack}
        onKeyDown={onKeyDown}
      >
        <span
          className={`${styles.gaTrackFill} ${dragging ? "" : styles.gaKnobAnimate}`}
          style={{ width: x > 0 ? x + KNOB_SIZE / 2 : 0 }}
        />
        <span className={styles.gaTrackEnd}><LockLineIcon /></span>
        <span
          ref={knobRef}
          className={`${styles.gaKnob} ${dragging ? "" : styles.gaKnobAnimate}`}
          style={{ transform: `translate(${x}px, -50%)` }}
          onPointerDown={onPointerDown}
          onPointerMove={onPointerMove}
          onPointerUp={endDrag}
          onPointerCancel={endDrag}
        >
          <KeyIcon />
        </span>
      </div>)}
  </div>);
};

/*
 * Walk-in door: press and hold the button for 1s to unlock; letting go early cancels.
 * Styled after the tenant app's DoorPanel + HoldToOpen. onOpen() -> Promise<boolean>; the parent shows errors.
 */
export const DoorControl = ({ onOpen }) => {
  // idle | holding | unlocking | unlocked
  const [phase, setPhase] = useState("idle");
  const [secondsLeft, setSecondsLeft] = useState(DOOR_RELOCK_SECONDS);
  const phaseRef = useRef("idle");
  const holdTimer = useRef(null);
  const { mounted, later } = useSafeTimers();

  const setBoth = (value) => {
    phaseRef.current = value;
    setPhase(value);
  };

  // Re-lock countdown shown while the door is unlocked
  useEffect(() => {
    if (phase !== "unlocked") return undefined;
    setSecondsLeft(DOOR_RELOCK_SECONDS);
    const id = setInterval(() => {
      setSecondsLeft((s) => {
        if (s <= 1) {
          clearInterval(id);
          setBoth("idle");
          return DOOR_RELOCK_SECONDS;
        }
        return s - 1;
      });
    }, 1000);
    return () => clearInterval(id);
  }, [phase]);

  const fire = async () => {
    holdTimer.current = null;
    setBoth("unlocking");
    const ok = await onOpen();
    if (!mounted.current) return;
    setBoth(ok ? "unlocked" : "idle");
  };

  const startHold = () => {
    if (phaseRef.current !== "idle") return;
    setBoth("holding");
    holdTimer.current = later(fire, HOLD_MS);
  };

  const cancelHold = () => {
    if (phaseRef.current !== "holding") return;
    clearTimeout(holdTimer.current);
    holdTimer.current = null;
    setBoth("idle");
  };

  const onPointerDown = (e) => {
    e.preventDefault();
    e.currentTarget.setPointerCapture?.(e.pointerId);
    startHold();
  };

  const onKeyDown = (e) => {
    if ((e.key === "Enter" || e.key === " ") && !e.repeat) {
      e.preventDefault();
      startHold();
    }
  };

  const onKeyUp = (e) => {
    if (e.key === "Enter" || e.key === " ") cancelHold();
  };

  const unlocked = phase === "unlocked";
  const ringClass = { holding: styles.gaHolding, unlocking: styles.gaHoldBusy, unlocked: styles.gaHoldDone }[phase] || "";
  const title = { idle: "Hold to open", holding: "Keep holding…", unlocking: "Unlocking…", unlocked: "Door unlocked — push to enter" }[phase];

  return (<div className={styles.gaControl}>
    <div className={styles.gaDevice}>
      <span className={styles.gaDeviceIcon}><DoorIcon /></span>
      <div>
        <p className={styles.gaStatus}>
          <span className={`${styles.gaDot} ${unlocked ? styles.gaDotOn : ""}`} />{unlocked ? "Open" : "Locked"}
        </p>
        <p className={styles.gaDeviceName}>Gate Door</p>
        <p className={styles.gaDeviceSub}>Walk-in door beside the gate</p>
      </div>
    </div>

    <div className={styles.gaHoldBox}>
      <button
        type="button"
        aria-label="Press and hold to unlock the door"
        className={`${styles.gaHold} ${ringClass}`}
        disabled={phase === "unlocking" || unlocked}
        onPointerDown={onPointerDown}
        onPointerUp={cancelHold}
        onPointerCancel={cancelHold}
        onPointerLeave={cancelHold}
        onKeyDown={onKeyDown}
        onKeyUp={onKeyUp}
        onBlur={cancelHold}
        onContextMenu={(e) => e.preventDefault()}
      >
        <svg className={styles.gaHoldRing} viewBox="0 0 76 76" aria-hidden="true">
          <circle className={styles.gaRingBg} cx="38" cy="38" r="33" />
          <circle className={styles.gaRingFg} cx="38" cy="38" r="33" />
        </svg>
        <span className={styles.gaHoldCore}>{unlocked ? <CheckIcon size={26} /> : <LockSolidIcon size={26} />}</span>
      </button>
      <div className={styles.gaHoldCopy} aria-live="polite">
        <p className={styles.gaHoldTitle}>{title}</p>
        <p className={styles.gaHint}>
          {unlocked
            ? (<>Re-locks automatically in <b>{secondsLeft}s</b>.</>)
            : (<>Press and hold for the pedestrian door beside the gate. Re-locks automatically after <b>{DOOR_RELOCK_SECONDS}s</b>.</>)}
        </p>
      </div>
    </div>
  </div>);
};
